import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { useNotification } from '../../../../../contexts/NotificationContext';
import { usePageLoaderContext } from '../../../../../contexts/PageLoaderContext';
import apiService from '../../../../../services/api';
import EmptyState from '../../../../UI/EmptyState';
import { OverviewTabProps, OverviewData } from './types';
import GroupInfoCard from './components/GroupInfoCard';
import OverviewStats from './components/OverviewStats';
import RecentActivityList from './components/RecentActivityList';
import TaskCompletionChart from './components/TaskCompletionChart';
import TaskStatusChart from './components/TaskStatusChart';
import TopPerformersChart from './components/TopPerformersChart';
import styles from '../../../../../styles/OverviewTab.module.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const OverviewTab: React.FC<OverviewTabProps> = ({ groupDetails, onGroupUpdate }) => {
  const notificationService = useNotification();
  const { showLoader, hideLoader } = usePageLoaderContext();

  const [overviewData, setOverviewData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (groupDetails.id) {
      loadOverviewData();
    }
  }, [groupDetails.id]);

  const loadOverviewData = async () => {
    setLoading(true);
    setError(null);
    showLoader('Loading group overview...');

    try {
      const response = await apiService.getGroupOverviewData(groupDetails.id);

      if (response.status === 'success') {
        setOverviewData(response.data);
      } else {
        setError(response.message || 'Failed to load overview data');
        notificationService.error(response.message || 'Failed to load overview data');
      }
    } catch (error: any) {
      console.error('Error loading overview data:', error);
      setError(error.response?.data?.message || 'Failed to load overview data');
      notificationService.error(error.response?.data?.message || 'Failed to load overview data. Please try again.');
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  if (loading) {
    return (
      <div className={styles.overviewContainer}>
        {/* Group Info Card Skeleton */}
        <div className={styles.groupInfoCardSkeleton}>
          <div className={styles.skeletonBanner}></div>
          <div className={styles.skeletonProfileSection}>
            <div className={styles.skeletonAvatar}></div>
            <div className={styles.skeletonDetails}>
              <div className={styles.skeletonTitle}></div>
              <div className={styles.skeletonDescription}></div>
              <div className={styles.skeletonMeta}>
                <div className={styles.skeletonMetaItem}></div>
                <div className={styles.skeletonMetaItem}></div>
                <div className={styles.skeletonMetaItem}></div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid Skeleton */}
        <div className={styles.statsGrid}>
          {Array.from({ length: 4 }, (_, index) => (
            <div key={index} className={styles.statCardSkeleton}>
              <div className={styles.skeletonStatHeader}>
                <div className={styles.skeletonStatTitle}></div>
                <div className={styles.skeletonStatIcon}></div>
              </div>
              <div className={styles.skeletonStatValue}></div>
              <div className={styles.skeletonStatChange}></div>
            </div>
          ))}
        </div>

        {/* Charts Skeleton */}
        <div className={styles.overviewGrid}>
          <div className={styles.overviewCardSkeleton}>
            <div className={styles.skeletonCardTitle}></div>
            <div className={styles.skeletonChart}></div>
          </div>
          <div className={styles.overviewCardSkeleton}>
            <div className={styles.skeletonCardTitle}></div>
            <div className={styles.skeletonChart}></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.overviewContainer}>
        <EmptyState
          icon={
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
          }
          title="Failed to Load Overview Data"
          description={error}
          action={{
            label: "Try Again",
            onClick: loadOverviewData
          }}
        />
      </div>
    );
  }

  if (!overviewData) {
    return null;
  }

  return (
    <div className={styles.overviewContainer}>
      {/* Group Info Card */}
      <GroupInfoCard groupDetails={groupDetails} />

      {/* Overview Header */}
      <div className={styles.overviewHeader}>
        <h2 className={styles.overviewTitle}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7"></rect>
            <rect x="14" y="3" width="7" height="7"></rect>
            <rect x="14" y="14" width="7" height="7"></rect>
            <rect x="3" y="14" width="7" height="7"></rect>
          </svg>
          Group Dashboard
        </h2>
        <p className={styles.overviewSubtitle}>
          Comprehensive overview of your group's performance, activity, and key metrics
        </p>
      </div>

      {/* Statistics Grid */}
      <OverviewStats stats={overviewData.group_stats} />

      {/* Overview Grid */}
      <div className={styles.overviewGrid}>
        <RecentActivityList
          activities={overviewData.recent_activities}
          userRole={overviewData.user_role}
        />
        <TaskCompletionChart data={overviewData.task_completion_data} />
      </div>

      {/* Charts Grid */}
      <div className={styles.overviewGrid}>
        <TaskStatusChart stats={overviewData.group_stats} />
        <TopPerformersChart data={overviewData.member_activity_data} />
      </div>
    </div>
  );
};

export default OverviewTab;

